import React, { useMemo, useState } from "react";
import {
  AHIcon,
  AHSeparator,
  AHTag,
  AHText,
  Box,
  Circle,
  ListItem,
  TouchableBox,
  useTheme,
} from "arkh";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";

import { ProductDetailsSection$key } from "./__generated__/ProductDetailsSection.graphql";
import { ProductDetailsRow$key } from "./__generated__/ProductDetailsRow.graphql";
import { ProductDetailsComponent_ProductDetails$key } from "./__generated__/ProductDetailsComponent_ProductDetails.graphql";
import { ProductDetailsRowText$key } from "./__generated__/ProductDetailsRowText.graphql";
import { ProductDetailsRowLongText$key } from "./__generated__/ProductDetailsRowLongText.graphql";
import { ProductDetailsRowNumber$key } from "./__generated__/ProductDetailsRowNumber.graphql";

import { ProductDetailsRowTags$key } from "./__generated__/ProductDetailsRowTags.graphql";
import { FormattedMessage, FormattedNumber } from "react-intl";

import { ProductDetailsStoreProductInfo$key } from "./__generated__/ProductDetailsStoreProductInfo.graphql";
import { ProductDetailsPromotions$key } from "./__generated__/ProductDetailsPromotions.graphql";
import { ProductDetailsSpecialPriceDates$key } from "./__generated__/ProductDetailsSpecialPriceDates.graphql";
import { StoreFormattedDate } from "../../common/dates/StoreFormattedDate";
import { ProductDetailsStockInfo_StoreProduct$key } from "./__generated__/ProductDetailsStockInfo_StoreProduct.graphql";
import { useIntlTokens } from "@/src/components/mobile/useIntlTokens";
import {
  ActivityIndicator,
  View,
  Linking,
  Modal,
  Platform,
  SafeAreaView,
} from "react-native";

import { LazyQueryNetworkBoundrary } from "@/src/components/mobile/QueryNetworkBoundrary";

import { ProductDetailsComponent$key } from "./__generated__/ProductDetailsComponent.graphql";
import { ProductDetailsErrorFallback } from "./ProductDetailsErrorFallback";
import { ProductDetailsRowLink$key } from "./__generated__/ProductDetailsRowLink.graphql";
import { ProductDetailsRowStatus$key } from "./__generated__/ProductDetailsRowStatus.graphql";
import { ProductDetailsQuery } from "./__generated__/ProductDetailsQuery.graphql";
import { ProductInfoComponentDetail } from "./ProductInfoComponentDetail";
import { ProductInfoComponentDetail_ProductInfoComponentDetailSchema$key } from "./__generated__/ProductInfoComponentDetail_ProductInfoComponentDetailSchema.graphql";
import { WebModal } from "@/src/TopLevelNavigators/UserLoggedIn/WebModal";
import { WebModalHeader } from "../OrgHome/WorkspaceTodos/WorkspaceTodoEdit/WorkspaceTodoEditMain/WebModalHeader";
import { Header } from "@react-navigation/elements";
import { HeaderButton } from "@/src/components";

type Props = {
  productId: string;
  description?: string;
};

function ProductDetailsFallback(props: { description?: string }) {
  return (
    <>
      {props.description ? (
        <ListItem>
          <AHText type="subtitle" color="text-secondary">
            {props.description}
          </AHText>
        </ListItem>
      ) : null}
      <Box style={{ alignItems: "center" }} space="xlarge">
        <ActivityIndicator />
        <FormattedMessage
          defaultMessage="Out getting fresh{newLine} product information"
          description="ProductDetails Loading Message"
          values={{
            newLine: "\n",
          }}
        >
          {(text) => (
            <AHText
              type="subtitle"
              color="text-secondary"
              spaceTop="medium"
              style={{
                textAlign: "center",
              }}
            >
              {text}
            </AHText>
          )}
        </FormattedMessage>
      </Box>
    </>
  );
}

export function ProductDetails(props: Props) {
  return (
    <LazyQueryNetworkBoundrary
      fallback={<ProductDetailsFallback description={props.description} />}
      errorFallback={(errorFallbackProps) => (
        <ProductDetailsErrorFallback
          errorFallbackProps={errorFallbackProps}
          description={props.description}
        />
      )}
    >
      {(fetchKey) => (
        <InnerProductDetails fetchKey={fetchKey} productId={props.productId} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerProductDetails(props: Props & { fetchKey: string }) {
  const data = useLazyLoadQuery<ProductDetailsQuery>(
    graphql`
      query ProductDetailsQuery($id: ID!) {
        storeProduct(id: $id) {
          ...ProductDetailsComponent_ProductDetails
        }
      }
    `,
    {
      id: props.productId,
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  if (!data.storeProduct) {
    return null;
  }

  return <ProductDetailsComponent product={data.storeProduct} />;
}

export function ProductDetailsComponent(props: {
  product: ProductDetailsComponent_ProductDetails$key;
}) {
  const product = useFragment(
    graphql`
      fragment ProductDetailsComponent_ProductDetails on StoreProduct {
        description
        ...ProductDetailsStoreProductInfo
        components {
          __typename
          ...ProductDetailsComponent
          ... on ProductInfoRow {
            row {
              ... on ProductInfoComponentRowInterface {
                detail {
                  title
                  ...ProductInfoComponentDetail_ProductInfoComponentDetailSchema
                }
              }
            }
            __typename
          }
          ... on ProductInfoSection {
            rows {
              ... on ProductInfoComponentRowInterface {
                detail {
                  title
                  ...ProductInfoComponentDetail_ProductInfoComponentDetailSchema
                }
              }
            }
          }
        }
      }
    `,
    props.product
  );

  const [detailModal, setDetailModal] = useState<
    | null
    | (ProductInfoComponentDetail_ProductInfoComponentDetailSchema$key & {
        title: string | null;
      })
  >(null);
  const theme = useTheme();
  return (
    <>
      {product.description ? (
        <ListItem>
          <AHText type="subtitle" color="text-secondary">
            {product.description}
          </AHText>
        </ListItem>
      ) : null}
      <StoreProductInfo product={product} />
      <AHSeparator />
      {product.components?.map((component, i, arr) => (
        <ProductDetailComponent
          key={i + ""}
          component={component}
          beforeHasSeparator={
            i === 0 || arr[i - 1].__typename === "ProductInfoSection"
          }
          openRowDetails={
            component.__typename === "ProductInfoRow"
              ? component.row?.detail
                ? [
                    () => {
                      if (component.row?.detail) {
                        setDetailModal(component.row.detail);
                      }
                    },
                  ]
                : []
              : (component.rows?.map((row) =>
                  row?.detail
                    ? () => {
                        if (row.detail) {
                          setDetailModal(row.detail);
                        }
                      }
                    : null
                ) ?? [])
          }
        />
      ))}
      <Modal
        visible={!!detailModal}
        presentationStyle="pageSheet"
        animationType="slide"
        style={{ flex: 1 }}
        onRequestClose={() => setDetailModal(null)}
      >
        {Platform.OS === "web" ? (
          <WebModal>
            <Box style={{ flex: 1 }} color="surface-background-light">
              <WebModalHeader
                close={
                  <WebModalHeader.CloseButton
                    onPress={() => setDetailModal(null)}
                  />
                }
                title={<AHText type="title-big">{detailModal?.title}</AHText>}
              />
              {detailModal ? (
                <ProductInfoComponentDetail component={detailModal} />
              ) : null}
            </Box>
          </WebModal>
        ) : (
          <SafeAreaView
            style={{ flex: 1, backgroundColor: theme["surface-primary"] }}
          >
            <Box style={{ flex: 1 }} color="surface-primary">
              <Header
                headerLeft={() => (
                  <Box spaceLeft="small">
                    <HeaderButton
                      type="CLOSE"
                      onPress={() => setDetailModal(null)}
                    />
                  </Box>
                )}
                headerStyle={{
                  backgroundColor: theme["surface-primary"],
                }}
                title={detailModal?.title ?? ""}
                headerShadowVisible={true}
              />
              <AHSeparator />
              <Box style={{ flex: 1 }} color="surface-background-light">
                {detailModal ? (
                  <ProductInfoComponentDetail component={detailModal} />
                ) : null}
              </Box>
            </Box>
          </SafeAreaView>
        )}
      </Modal>
    </>
  );
}

function StoreProductInfo(props: {
  product: ProductDetailsStoreProductInfo$key;
}) {
  const product = useFragment(
    graphql`
      fragment ProductDetailsStoreProductInfo on StoreProduct {
        ...ProductDetailsStockInfo_StoreProduct
        regularPrice {
          value
          currency
        }
        specialPrices {
          ...ProductDetailsSpecialPriceDates
          price {
            currency
            value
          }
          loyaltyOnly
        }

        ...ProductDetailsPromotions
      }
    `,
    props.product
  );

  const val = product.regularPrice?.value;

  return (
    <Box color="surface-primary">
      <Box
        space="medium"
        spaceTop={
          !val && (product.specialPrices?.length ?? 0) === 0 ? "none" : "medium"
        }
        spaceBottom="none"
      >
        {val ? (
          <AHText
            type={
              (product.specialPrices?.length ?? 0 > 0)
                ? "title-small"
                : "title-big"
            }
            spaceBottom={
              (product.specialPrices?.length ?? 0 > 0) ? "xsmall" : "medium"
            }
          >
            <FormattedNumber
              value={val}
              currency={product.regularPrice?.currency}
              style="currency"
              minimumFractionDigits={Number.isInteger(val) ? 0 : 2}
              maximumFractionDigits={3}
              currencyDisplay="narrowSymbol"
            />
          </AHText>
        ) : null}
        <Box>
          {product.specialPrices?.map((sp, i) => (
            <Box
              key={i + ""}
              style={{ flexDirection: "row" }}
              spaceBottom="medium"
            >
              <Box style={{ flexDirection: "column" }}>
                <AHText type="title-big" color="negative">
                  <FormattedNumber
                    value={sp.price?.value ?? 0}
                    currency={sp.price?.currency}
                    style="currency"
                    minimumFractionDigits={
                      Number.isInteger(sp.price?.value ?? 0) ? 0 : 2
                    }
                    maximumFractionDigits={3}
                    currencyDisplay="narrowSymbol"
                  />
                </AHText>
                <SpecialPriceDates specialPrice={sp} />
              </Box>
              {sp.loyaltyOnly ? <LoyaltyBadge /> : null}
            </Box>
          ))}
        </Box>
      </Box>
      <StockInfo product={product} />
      <Promotions product={product} />
    </Box>
  );
}

function StockIndicator(props: { availableQuantity: number }) {
  return (
    <Box style={{ flexDirection: "row", alignItems: "center" }}>
      <Circle
        size="dot"
        color={props.availableQuantity > 0 ? "positive" : "negative"}
      />
      <AHText type="subtitle" spaceLeft="small">
        {props.availableQuantity}
      </AHText>
    </Box>
  );
}

function StockInfoListItem(props: { name: string; availableQuantity: number }) {
  return (
    <ListItem
      right={<StockIndicator availableQuantity={props.availableQuantity} />}
    >
      <Label>{props.name}</Label>
    </ListItem>
  );
}

function StockInfo(props: {
  product: ProductDetailsStockInfo_StoreProduct$key;
}) {
  const product = useFragment(
    graphql`
      fragment ProductDetailsStockInfo_StoreProduct on StoreProduct {
        stockInfo {
          store {
            quantity
          }
          otherLocations {
            name
            availableQuantity
          }
        }
      }
    `,
    props.product
  );
  const [expanded, setExpanded] = useState(false);
  const intl_tokens = useIntlTokens();
  if (!product.stockInfo) {
    return null;
  }

  const storeStock = product.stockInfo.store?.quantity ?? 0;

  return (
    <Box>
      <ListItem
        onPress={() => {
          setExpanded((p) => !p);
        }}
        right={
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            {expanded ? null : (
              <StockIndicator availableQuantity={storeStock} />
            )}
            <AHIcon
              spaceLeft="small"
              type={expanded ? "chevron-up" : "chevron-down"}
            />
          </View>
        }
      >
        <AHText type="subtitle" selectable={false} color="text-primary">
          <FormattedMessage defaultMessage="Stock" />
        </AHText>
      </ListItem>
      {expanded ? (
        <Box>
          <StockInfoListItem
            name={intl_tokens.Unit}
            availableQuantity={storeStock}
          />
          {product.stockInfo?.otherLocations?.map((row) =>
            row ? (
              <>
                <StockInfoListItem
                  name={row.name ?? ""}
                  availableQuantity={row.availableQuantity ?? 0}
                />
              </>
            ) : null
          )}
        </Box>
      ) : null}
    </Box>
  );
}

function SpecialPriceDates(props: {
  specialPrice: ProductDetailsSpecialPriceDates$key;
}) {
  const product = useFragment(
    graphql`
      fragment ProductDetailsSpecialPriceDates on ProductStoreSpecialPrice {
        startAtDate {
          ...StoreFormattedDate @arguments(style: SHORT_DATE)
        }
        endAtDate {
          ...StoreFormattedDate @arguments(style: SHORT_DATE)
        }
      }
    `,
    props.specialPrice
  );
  if (!product.startAtDate && !product.endAtDate) {
    return null;
  }

  return (
    <AHText type="subtitle" color="text-secondary">
      {product.startAtDate ? (
        <>
          <StoreFormattedDate date={product.startAtDate} />{" "}
        </>
      ) : null}
      {"-> "}
      {product.endAtDate ? (
        <StoreFormattedDate date={product.endAtDate} />
      ) : null}
    </AHText>
  );
}

function Promotions(props: { product: ProductDetailsPromotions$key }) {
  const product = useFragment(
    graphql`
      fragment ProductDetailsPromotions on StoreProduct {
        promotions {
          hasActivePromotion
        }
      }
    `,
    props.product
  );

  if (!product.promotions?.hasActivePromotion) {
    return null;
  }

  return (
    <Box space="medium" color="surface-background-negative">
      <AHText type="subtitle" color="negative">
        <FormattedMessage
          defaultMessage="Active Promotion"
          description="ProductDetails Active Promotion Label"
        />
      </AHText>
    </Box>
  );
}

function LoyaltyBadge() {
  return (
    <Box spaceLeft="medium">
      <Box
        color="primary"
        space="xsmall"
        spaceHorizontal="small"
        cornerRadius="medium"
      >
        <AHText type="subtitle" color="text-inverted">
          Member
        </AHText>
      </Box>
    </Box>
  );
}

function SubTitle(props: { children: React.ReactNode }) {
  return (
    <AHText type="info-small" color="text-secondary" spaceTop="xxsmall">
      {props.children}
    </AHText>
  );
}

function Label(props: { children: React.ReactNode }) {
  return (
    <AHText type="subtitle" color="text-secondary">
      {props.children}
    </AHText>
  );
}

function Detail(props: { children: React.ReactNode }) {
  return (
    <AHText type="detail" spaceRight="xsmall">
      {props.children}
    </AHText>
  );
}

function Text(props: { children: React.ReactNode }) {
  return <AHText type="body-big">{props.children}</AHText>;
}

export function ProductDetailComponent(props: {
  component: ProductDetailsComponent$key;
  beforeHasSeparator?: boolean;
  openRowDetails?: ((() => void) | null)[];
}) {
  const component = useFragment(
    graphql`
      fragment ProductDetailsComponent on ProductStoreInfoComponent {
        ... on ProductInfoRow {
          row {
            ...ProductDetailsRow
          }

          __typename
        }
        ... on ProductInfoSection {
          ...ProductDetailsSection
          __typename
        }
      }
    `,
    props.component
  );

  switch (component.__typename) {
    case "ProductInfoRow":
      if (!component.row) {
        return null;
      }
      return (
        <ProductDetailRow
          row={component.row}
          openRowDetail={props.openRowDetails?.[0] ?? undefined}
        />
      );
    case "ProductInfoSection":
      return (
        <ProductDetailSection
          section={component}
          beforeHasSeparator={props.beforeHasSeparator}
          openRowDetails={props.openRowDetails}
        />
      );
  }
  return null;
}

function ProductDetailRow(props: {
  row: ProductDetailsRow$key;
  openRowDetail?: () => void;
}) {
  const row = useFragment(
    graphql`
      fragment ProductDetailsRow on ProductInfoComponentRow {
        ... on ProductInfoComponentNumberRow {
          ...ProductDetailsRowNumber
          __typename
        }
        ... on ProductInfoComponentTextRow {
          ...ProductDetailsRowText
          __typename
        }
        ... on ProductInfoComponentLongTextRow {
          ...ProductDetailsRowLongText
          __typename
        }
        ... on ProductInfoComponentTagsRow {
          ...ProductDetailsRowTags
          __typename
        }
        ... on ProductInfoComponentLinkRow {
          ...ProductDetailsRowLink
          __typename
        }
        ... on ProductInfoComponentStatusRow {
          ...ProductDetailsRowStatus
          __typename
        }
      }
    `,
    props.row
  );
  switch (row?.__typename) {
    case "ProductInfoComponentTextRow":
      return (
        <ProductDetailRowText row={row} openRowDetail={props.openRowDetail} />
      );
    case "ProductInfoComponentLongTextRow":
      return (
        <ProductDetailRowLongText
          row={row}
          openRowDetail={props.openRowDetail}
        />
      );
    case "ProductInfoComponentNumberRow":
      return (
        <ProductDetailRowNumber row={row} openRowDetail={props.openRowDetail} />
      );
    case "ProductInfoComponentTagsRow":
      return (
        <ProductDetailRowTags row={row} openRowDetail={props.openRowDetail} />
      );
    case "ProductInfoComponentLinkRow":
      return (
        <ProductDetailRowLink row={row} openRowDetail={props.openRowDetail} />
      );
    case "ProductInfoComponentStatusRow":
      return (
        <ProductDetailRowStatus row={row} openRowDetail={props.openRowDetail} />
      );
  }
  return null;
}

function ProductDetailRowLink(props: {
  row: ProductDetailsRowLink$key;
  openRowDetail?: () => void;
}) {
  const row = useFragment(
    graphql`
      fragment ProductDetailsRowLink on ProductInfoComponentLinkRow {
        label
        subTitle
        link {
          label
          url
        }
      }
    `,
    props.row
  );
  return (
    <ProductRowListItem
      onOpenDetail={props.openRowDetail}
      right={
        <TouchableBox
          hitSlop={{ left: 50, top: 15, bottom: 15 }}
          pressRetentionOffset={{ left: 50, top: 15, bottom: 15 }}
          spaceLeft="medium"
          disabled={!row.link?.url}
          onPress={(e) => {
            e.stopPropagation();
            if (row.link?.url) {
              Linking.openURL(row.link.url);
            }
          }}
          style={{ flexDirection: "row", alignItems: "center", height: "100%" }}
        >
          <Detail>{row.link?.label}</Detail>
          <AHIcon type="link" color="text-secondary" size="small" />
        </TouchableBox>
      }
    >
      <Label>{row.label}</Label>
      {row.subTitle ? <SubTitle>{row.subTitle}</SubTitle> : null}
    </ProductRowListItem>
  );
}

function ProductRowListItem(props: {
  onOpenDetail?: (() => void) | undefined;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <ListItem
      disabled={!props.onOpenDetail}
      onPress={props.onOpenDetail}
      right={
        !!props.onOpenDetail ? (
          <Box
            style={{
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            {props.right}
            <AHIcon spaceLeft="xsmall" type="chevron-right" />
          </Box>
        ) : (
          props.right
        )
      }
    >
      {props.children}
    </ListItem>
  );
}

function useParseColor(color: string | undefined) {
  const theme = useTheme();

  return useMemo(() => {
    if (!color) {
      return undefined;
    }
    const lowerCase = color.toLowerCase();
    switch (lowerCase) {
      case "positive":
        return theme.positive;
      case "negative":
        return theme.negative;
      case "semi-negative":
      case "warning":
        return theme.semiNegative;
      case "primary":
        return theme.primary;
    }
    return color;
  }, [color, theme]);
}

function ProductDetailRowStatus(props: {
  row: ProductDetailsRowStatus$key;
  openRowDetail?: () => void;
}) {
  const row = useFragment(
    graphql`
      fragment ProductDetailsRowStatus on ProductInfoComponentStatusRow {
        label
        subTitle
        status {
          color
          label
        }
      }
    `,
    props.row
  );
  const color = useParseColor(row.status?.color ?? undefined);
  return (
    <ProductRowListItem
      onOpenDetail={props.openRowDetail}
      right={
        <>
          <Detail>{row.status?.label}</Detail>
          <Circle size="dot" style={{ backgroundColor: color ?? "#000" }} />
        </>
      }
    >
      <Label>{row.label}</Label>
      {row.subTitle ? <SubTitle>{row.subTitle}</SubTitle> : null}
    </ProductRowListItem>
  );
}

function ProductDetailRowTags(props: {
  row: ProductDetailsRowTags$key;
  openRowDetail?: () => void;
}) {
  const row = useFragment(
    graphql`
      fragment ProductDetailsRowTags on ProductInfoComponentTagsRow {
        label
        subTitle
        tags {
          color
          label
        }
      }
    `,
    props.row
  );
  return (
    <ProductRowListItem
      onOpenDetail={props.openRowDetail}
      right={row.tags?.map((tag, i) => (
        <AHTag color={tag?.color ?? "#000"} title={tag?.label ?? ""} key={i} />
      ))}
    >
      <Label>{row.label}</Label>
      {row.subTitle ? <SubTitle>{row.subTitle}</SubTitle> : null}
    </ProductRowListItem>
  );
}

function ProductDetailRowText(props: {
  row: ProductDetailsRowText$key;
  openRowDetail?: () => void;
}) {
  const row = useFragment(
    graphql`
      fragment ProductDetailsRowText on ProductInfoComponentTextRow {
        text
        label
        subTitle
      }
    `,
    props.row
  );
  return (
    <ProductRowListItem
      onOpenDetail={props.openRowDetail}
      right={<Text>{row.text}</Text>}
    >
      <Label>{row.label}</Label>
      {row.subTitle ? <SubTitle>{row.subTitle}</SubTitle> : null}
    </ProductRowListItem>
  );
}

function ProductDetailRowLongText(props: {
  row: ProductDetailsRowLongText$key;
  openRowDetail?: () => void;
}) {
  const row = useFragment(
    graphql`
      fragment ProductDetailsRowLongText on ProductInfoComponentLongTextRow {
        text
        label
        subTitle
      }
    `,
    props.row
  );
  return (
    <ProductRowListItem
      onOpenDetail={props.openRowDetail}
      right={<Text>{row.text}</Text>}
    >
      <Label>{row.label}</Label>
      {row.subTitle ? <SubTitle>{row.subTitle}</SubTitle> : null}
    </ProductRowListItem>
  );
}

function ProductDetailRowNumber(props: {
  row: ProductDetailsRowNumber$key;
  openRowDetail?: () => void;
}) {
  const row = useFragment(
    graphql`
      fragment ProductDetailsRowNumber on ProductInfoComponentNumberRow {
        number
        label
        subTitle
      }
    `,
    props.row
  );
  return (
    <ProductRowListItem
      onOpenDetail={props.openRowDetail}
      right={
        <Text>
          {row.number ? <FormattedNumber value={row.number} /> : null}
        </Text>
      }
    >
      <Label>{row.label}</Label>
      {row.subTitle ? <SubTitle>{row.subTitle}</SubTitle> : null}
    </ProductRowListItem>
  );
}

function ProductDetailSection(props: {
  section: ProductDetailsSection$key;
  beforeHasSeparator?: boolean;
  openRowDetails?: ((() => void) | null)[];
}) {
  const section = useFragment(
    graphql`
      fragment ProductDetailsSection on ProductInfoSection {
        label
        rows {
          ...ProductDetailsRow
        }
        __typename
      }
    `,
    props.section
  );

  const [expanded, setExpanded] = useState(false);

  return (
    <>
      {!props.beforeHasSeparator ? <AHSeparator /> : null}
      <Box>
        <ListItem
          onPress={() => {
            setExpanded((p) => !p);
          }}
          right={<AHIcon type={expanded ? "chevron-up" : "chevron-down"} />}
        >
          <AHText
            type="subtitle"
            selectable={false}
            color={expanded ? "text-primary" : "text-secondary"}
          >
            {section.label}
          </AHText>
        </ListItem>
        {expanded ? (
          <Box>
            {section.rows?.map((row, index) =>
              row ? (
                <ProductDetailRow
                  key={index + ""}
                  row={row}
                  openRowDetail={props.openRowDetails?.[index] ?? undefined}
                />
              ) : null
            )}
          </Box>
        ) : null}
      </Box>
      <AHSeparator />
    </>
  );
}
